import * as React from 'react';
import { useWizardContext } from './context';
import { IWizardNavigationArgs } from './instance';
import { Assign, InferProps } from '../../utils/types';

type WizardStepEventHandlers = {
    // TODO: find if exposing activated event through this hook is legible
    // activated(args: { lastStep: string; }): void;
    deactivating(args: { nextStep: string; }): void;
    // removed(): void;
};

export type WizardStepEventsProxy = <Key extends keyof WizardStepEventHandlers>(eventName: Key, handler: WizardStepEventHandlers[Key]) => () => void;

export const useWizardStep = (stepId: string) => {

    const { state, selectors, actions: { navigate, saveStepData, clearAllStepsData }, eventManager } = useWizardContext();

    const next = React.useCallback((args?: IWizardNavigationArgs) => navigate(selectors.nextStepId(stepId), args), [navigate, selectors, stepId]);

    const previous = React.useCallback((args?: IWizardNavigationArgs) => navigate(selectors.previousStepId(stepId), args), [navigate, selectors, stepId]);

    const eventsProxy = React.useMemo<WizardStepEventsProxy>(() => (name, handler) => {
        const eventName = `#${stepId}:${name}`;
        eventManager.on(eventName, handler);
        return () => eventManager.off(eventName, handler);
    }, [stepId, eventManager]);

    const saveData = React.useCallback((data: any) => saveStepData(stepId, data), [stepId, saveStepData]);

    const actions = React.useMemo(() => ({ next, previous, saveData, clearAllStepsData }), [next, previous, saveData, clearAllStepsData]);

    const isNavigating = React.useMemo(() => state.navigationStatus === 'navigating', [state.navigationStatus]);

    const derivedState = React.useMemo(() => {
        const isActive = selectors.isActive(stepId);
        const isFirst = selectors.isFirst(stepId);
        const isLast = selectors.isLast(stepId);

        const isBetween = selectors.isBetween(stepId);
        const hasVisited = selectors.hasVisited(stepId);
        const params = selectors.getStepParams(stepId);

        return {
            isActive, isFirst, isLast, isBetween, isNavigating, hasVisited,
            data: selectors.getStepData(stepId), params,
            on: eventsProxy,
            ...actions
        };

    }, [selectors, stepId, isNavigating, eventsProxy, actions]);

    return derivedState;

};

export interface IWizardStepRendererProps {
    stepId: string;
}


export type WizardStepProps<
    TComponent extends React.ComponentType<IWizardStepRendererProps>
    > = Assign<
        {

            id: string;

            /**
             * the component to be used to render the step UI
             */
            component: TComponent;
        },
        Omit<InferProps<TComponent>, 'stepId' | 'children'>
    >;

export const WizardStep = <TComponent extends
    | React.ComponentType<any>
>({ id, component, ...rest }: WizardStepProps<TComponent>) => {
    const { actions: { addStep, removeStep } } = useWizardContext();
    const { isActive } = useWizardStep(id);

    React.useEffect(() => {
        addStep({ id });
        return () => {
            removeStep(id);
        };
    }, [id, addStep, removeStep]);

    return isActive ? React.createElement(component, { stepId: id, ...rest }) : null;
};
